import {
  Events,
  IStatusPanelComp,
  IStatusPanelParams,
  Module,
  StatusPanelDef,
} from '@ag-grid-community/core'
import { loadTemplate } from '@ag-grid-community/core/dist/cjs/utils/dom'
import { EnterpriseCoreModule } from '@ag-grid-enterprise/core'
import { StatusBarModule } from '@ag-grid-enterprise/status-bar'

/**
 * Ambient state the Position Detail status panels read off `gridOptions.context`.
 *
 * Both counts come from the sub-panel's own view model rather than from the grid: `fields` could be
 * read as `api.getDisplayedRowCount()`, but `total` (what the row holds before any filter) has no
 * grid-side equivalent, and sourcing the pair from one place is what stops them disagreeing while a
 * filter is being applied. The object is mutated in place — see the note on
 * {@link WorkbenchGridContext}, which does the same for the two grids above.
 */
export interface DetailGridContext {
  /** Rows on screen now; moves with the group filter, hide-empty toggle and search. */
  fields: number
  /** What the row actually has, after the Helios drop and before any filter. Never moves. */
  total: number
}

/**
 * Reuses the same class names as the workbench's own `Rows:` panel so these inherit the global
 * `.ag-status-bar` rules in `apps/helios/src/styles.scss` and read identically to every other
 * Helios grid footer.
 */
const template = (label: string): string => `
  <span class="hs-status-bar hs-total-rows-status-bar">
    <span>${label}:</span> <span class="value ml-1">0</span>
  </span>
`

/**
 * A labelled count in the grid's own footer. Refreshes on MODEL_UPDATED, which the `[data]` binding
 * fires on every view-model emission — the same emission that wrote the context.
 */
abstract class DetailCountStatusBarComp implements IStatusPanelComp {
  private readonly eGui: HTMLElement

  private params?: IStatusPanelParams

  /** @internal */
  readonly modelUpdatedListener = () => this.refresh()

  protected constructor(label: string) {
    this.eGui = loadTemplate(template(label))
  }

  protected abstract count(context: DetailGridContext): number

  init(params: IStatusPanelParams): void {
    this.params = params
    params.api.addEventListener(
      Events.EVENT_MODEL_UPDATED,
      this.modelUpdatedListener,
    )
    this.refresh()
  }

  destroy(): void {
    this.params?.api.removeEventListener(
      Events.EVENT_MODEL_UPDATED,
      this.modelUpdatedListener,
    )
  }

  getGui(): HTMLElement {
    return this.eGui
  }

  /** @internal */
  refresh(): void {
    const value = this.eGui.querySelector('.value')
    if (value == null) return
    const context = this.params?.context as DetailGridContext | undefined
    value.textContent = `${context ? this.count(context) : 0}`
  }
}

export class DetailFieldsStatusBarComp extends DetailCountStatusBarComp {
  constructor() {
    super('Fields')
  }

  protected count(context: DetailGridContext): number {
    return context.fields
  }
}

export class DetailTotalStatusBarComp extends DetailCountStatusBarComp {
  constructor() {
    super('Total')
  }

  protected count(context: DetailGridContext): number {
    return context.total
  }
}

export const detailStatusBarModuleName = 'hsPositionDetailStatusBarModule'
export const detailFieldsStatusBar = 'hsPositionDetailFieldsStatusBar'
export const detailTotalStatusBar = 'hsPositionDetailTotalStatusBar'

/** Both counts sit at the left of the footer, in the order the design shows them. */
export const detailStatusBarPanel: { statusPanels: StatusPanelDef[] } = {
  statusPanels: [
    { statusPanel: detailFieldsStatusBar, align: 'left' },
    { statusPanel: detailTotalStatusBar, align: 'left' },
  ],
}

export const DetailStatusBarModule: Module = {
  moduleName: detailStatusBarModuleName,
  userComponents: [
    {
      componentName: detailFieldsStatusBar,
      componentClass: DetailFieldsStatusBarComp,
    },
    {
      componentName: detailTotalStatusBar,
      componentClass: DetailTotalStatusBarComp,
    },
  ],
  dependantModules: [StatusBarModule, EnterpriseCoreModule],
}
